// Use Vite's specific syntax for worker imports
import HighlightWorker from '../workers/highlight.worker.ts?worker';

interface JobMessage {
  id: number;
  lang: string;
  code: string;
}

interface Token {
  content: string;
  color?: string;
  fontStyle?: number;
}

interface ResultMessage {
  id: number;
  tokenLines: Token[][];
}

interface CacheEntry {
  promise: Promise<Token[][]>;
}

const cache = new Map<string, CacheEntry>();

// New hunk cache
const hunkCache = new Map<string, Promise<Token[][]>>();

const worker = new HighlightWorker();

let nextId = 1;
const callbacks = new Map<number, (tokens: Token[][]) => void>();

worker.addEventListener('error', (e) => {
   
  console.error('Worker error', e);
});
worker.addEventListener('messageerror', (e) => {
  console.error('Worker message error', e);
});

worker.onmessage = (e: MessageEvent<ResultMessage | ResultMessage[]>) => {
  // console.log('onmessage', e);
  const results = Array.isArray(e.data) ? e.data : [e.data];
  // console.log('highlight worker → main', results);
  for (const { id, tokenLines } of results) {
    const cb = callbacks.get(id);
    if (cb) {
      cb(tokenLines);
      callbacks.delete(id);
    }
  }
};

// Deprecated per-line helper (wraps first line)
export function highlightAsyncTokens(code: string, lang: string): Promise<Token[]> {
  // console.log('highlightAsyncTokens', code, lang);
  const key = `${lang}|${code}`;
  const existing = cache.get(key);
  if (existing) return existing.promise.then((lines) => lines[0] || []);

  const id = nextId++;
  const promise = new Promise<Token[][]>((resolve) => {
    callbacks.set(id, (tokenLines: Token[][]) => resolve(tokenLines));
    const msg: JobMessage = { id, lang, code };
    worker.postMessage(msg);
  }).then((lines) => lines[0] || []);

  cache.set(key, { promise: promise.then((tokens) => [tokens]) });
  return promise;
}

// New API: highlight entire hunk (multi-line)
export function highlightHunkTokens(key: string, lang: string, code: string): Promise<Token[][]> {
  const existing = hunkCache.get(key);
  if (existing) return existing;

  const id = nextId++;
  const prom = new Promise<Token[][]>((resolve) => {
    callbacks.set(id, (tokenLines: Token[][]) => resolve(tokenLines));
    const msg: JobMessage = { id, lang, code };
    worker.postMessage(msg);
  });
  hunkCache.set(key, prom);
  return prom;
}

// Export the Token type for use in components
export type { Token };
